import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryBuilderAntD, AntDValueEditor, AntDValueSelector } from '@react-querybuilder/antd';
import { formatQuery, QueryBuilder, defaultOperators } from 'react-querybuilder';
import { capitalize, upperFirst } from 'lodash';
import 'react-querybuilder/dist/query-builder.css';
import { QuestionCircleOutlined } from '@ant-design/icons';

import { deviceTypes, referrerTypes, browsers, deviceBrands, operatingSystems } from 'shared/constants/matomo';
import { QUESTION_TYPES } from 'shared/constants/survey';
import filterFields from 'shared/constants/filterFields';
import { isSameQuery } from 'shared/utils/queryBuilder';

import TechnologyIcon, { hasTechnologyIcons } from 'components/TechnologyIcon';
import CountrySelect from 'components/CountrySelect';
import { DeviceTypeIcon } from 'components/DeviceTypeIcons';
import useFormatMessage from 'hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import useMatomoGoalOptions, { getMatomoGoalLabel } from 'hooks/useMatomoGoalOptions';
import LanguageSelect from 'components/LanguageSelect';
import { Button, Select, Space } from 'antd';
import SurveyGroupSelect from 'components/SurveyGroupSelect';
import { getDimensionValue } from 'components/DimensionValue';
import goalMessages from 'components/DimensionValue/messages';
import { KB_REGEX_SYNTAX } from 'shared/constants/urls';
import messages from './messages';
import SurveyOptionSelector from './SurveyQuestionOptionSelector';
import SurveyQuestionSelector from './SurveyQuestionSelector';
import SegmentSelector from './SegmentSelector';
import GoalSelector from './GoalSelector';

import './query-builder.less';

export const emptyQuery = { combinator: 'and', rules: [{}] };

const multiSelectOperators = defaultOperators.filter((op) => ['in', 'notIn'].includes(op.name));
const matchesRegexOperator = { name: 'matchesRegex', label: 'matches regular expression', value: 'matchesRegex' };
const textOperators = [
  ...defaultOperators.filter((op) => ['=', '!=', 'contains', 'doesNotContain', 'beginsWith', 'endsWith', 'doesNotBeginWith', 'doesNotEndWith', 'null', 'notNull'].includes(op.name)),
  matchesRegexOperator,
];
const numberOperators = defaultOperators.filter((op) => ['=', '!=', '<', '<=', '>', '>=', 'between', 'notBetween', 'null', 'notNull'].includes(op.name));
const dateOperators = defaultOperators.filter((op) => ['between', 'notBetween'].includes(op.name));
const booleanOperators = defaultOperators.filter((op) => ['=', '!=', 'null', 'notNull'].includes(op.name));
const equalOperators = defaultOperators.filter((op) => op.name === '=');

const questionTypeValues = Object.keys(QUESTION_TYPES);
const deviceTypeValues = Object.keys(deviceTypes);
const browserValues = Object.keys(browsers);
const referrerTypeValues = Object.keys(referrerTypes);
const deviceBrandValues = Object.keys(deviceBrands);
const operatingSystemValues = Object.keys(operatingSystems);

const SearchableValueSelector = (props) => (
  <AntDValueSelector
    showSearch
    style={{ minWidth: 200 }}
    {...props}
  />
);

const inlineValueEditorTypes = ['deviceType', 'country', 'language', 'boolean', 'surveyGroup', 'surveyQuestion', 'surveyOption', 'segment', 'conversionGoal', 'number', 'date'];

const CustomValueEditor = (props) => {
  const { type, customerId } = props.fieldData;
  const $t = useFormatMessage();
  const isTextValueEditor = !inlineValueEditorTypes.includes(type);
  const isRegexOperator = props.operator === matchesRegexOperator.name;

  let component;

  if (hasTechnologyIcons(type)) {
    const options = Object.entries({ deviceBrand: deviceBrands, browser: browsers, operatingSystem: operatingSystems }[type]).map(([value, label]) => ({
      value,
      searchText: label,
      label: (
        <Space>
          <TechnologyIcon type={type} value={value} />
          {label}
        </Space>
      ),
    }));
    return <Select mode="multiple" showSearch optionFilterProp="searchText" value={props.value || []} onChange={props.handleOnChange} options={options} disabled={props.disabled} style={{ minWidth: 200 }} />;
  }

  switch (type) {
    case 'deviceType':
      component = (
        <Select
          mode="multiple"
          value={props.value}
          onChange={props.handleOnChange}
          style={{ minWidth: 200 }}
          optionFilterProp="searchText"
          options={deviceTypeValues.map(value => {
            const label = getDimensionValue({ $t, dimension: { dimension: 'deviceType' }, value });
            return {
              value,
              searchText: label,
              label: (
                <Space>
                  <DeviceTypeIcon value={value} />
                  {label}
                </Space>
              ),
            };
          })}
        />
      );
      break;
    case 'country':
      component = <CountrySelect value={props.value} onChange={props.handleOnChange} mode="multiple" style={{ minWidth: 200 }} />;
      break;
    case 'language':
      component = <LanguageSelect value={props.value} onChange={props.handleOnChange} mode="multiple" style={{ minWidth: 200 }} />;
      break;
    case 'boolean':
      component = <Select value={props.value} onChange={props.handleOnChange} options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} />;
      break;
    case 'surveyGroup':
      component = <SurveyGroupSelect value={props.value} onChange={props.handleOnChange} customerId={customerId} style={{ minWidth: 200 }} multiple />;
      break;
    case 'surveyQuestion':
      component = <SurveyQuestionSelector value={props.value} onChange={props.handleOnChange} customerId={customerId} />;
      break;
    case 'surveyOption':
      component = <SurveyOptionSelector value={props.value} onChange={props.handleOnChange} customerId={customerId} />;
      break;
    case 'segment':
      component = <SegmentSelector value={props.value} onChange={props.handleOnChange} customerId={customerId} />;
      break;
    case 'conversionGoal':
      component = <GoalSelector value={props.value} onChange={props.handleOnChange} customerId={customerId} />;
      break;
    default:
  }

  if (component) {
    return (
      <div>
        {component}
      </div>
    );
  }

  return (
    <div className={isTextValueEditor ? 'queryBuilder-textValueEditor' : undefined}>
      <AntDValueEditor
        extraProps={{
          style: isTextValueEditor ? { width: '100%' } : { minWidth: 90 },
        }}
        {...props}
      />
      {isTextValueEditor && isRegexOperator && (
        <div className="queryBuilder-regexHelp">
          <a href={KB_REGEX_SYNTAX} target="_blank" rel="noreferrer">
            <Button icon={<QuestionCircleOutlined />} type="link" size="small" style={{ fontSize: 12 }}>
              {$t(messages.regexSyntaxLabel)}
            </Button>
          </a>
        </div>
      )}
    </div>
  );
};

const getDefaultValue = (rule, { fieldData }) => {
  switch (fieldData.type) {
    case 'country':
    case 'language':
    case 'questionType':
    case 'deviceType':
    case 'deviceBrand':
    case 'operatingSystem':
    case 'browser':
    case 'referrerType':
    case 'surveyGroup':
    case 'surveyQuestion':
    case 'surveyOption':
      return [];
    case 'boolean':
      return true;
    case 'date':
      return [new Date(), new Date()];
    default:
      return '';
  }
};

export function getFieldValueLabel(field, value, $t, goalById, goalLoading = false) {
  const type = filterFields[field]?.type;
  switch (type) {
    case 'conversionGoal':
      return getMatomoGoalLabel(goalById, value, {
        deletedLabel: $t(goalMessages.deletedGoal),
        loading: goalLoading,
      });
    case 'operatingSystem':
      return operatingSystems[value] || value;
    case 'browser':
      return browsers[value] || value;
    case 'deviceBrand':
      return deviceBrands[value] || value;
    default:
      return getDimensionValue({ $t, dimension: { dimension: field, type }, value });
  }
}

const getFieldProps = ({ field, fieldConfig, $t, goalOptions }) => {
  let operators = textOperators;
  let valueEditorType = 'text';
  let inputType;
  let dataType;
  let values;

  switch (fieldConfig.type) {
    case 'country':
      operators = multiSelectOperators;
      break;
    case 'language':
      operators = multiSelectOperators;
      break;
    case 'number':
      inputType = 'number';
      operators = numberOperators;
      break;
    case 'boolean':
      operators = booleanOperators;
      break;
    case 'date':
      inputType = 'date';
      dataType = 'date';
      operators = dateOperators;
      break;
    case 'questionType':
      values = questionTypeValues;
      break;
    case 'deviceType':
      values = deviceTypeValues;
      break;
    case 'deviceBrand':
      values = deviceBrandValues;
      break;
    case 'operatingSystem':
      values = operatingSystemValues;
      break;
    case 'browser':
      values = browserValues;
      break;
    case 'referrerType':
      values = referrerTypeValues;
      break;
    case 'surveyGroup':
    case 'surveyQuestion':
    case 'surveyOption':
      operators = multiSelectOperators;
      break;
    case 'conversionGoal':
      operators = equalOperators;
      valueEditorType = 'select';
      values = goalOptions?.map(({ label, value: goalId }) => ({
        name: goalId,
        label,
      })) ?? [];
      break;

    default:
  }

  if (values && fieldConfig.type !== 'conversionGoal') {
    operators = multiSelectOperators;
    valueEditorType = 'multiselect';
    values = values.map((value) => ({
      name: value,
      label: getFieldValueLabel(field, value, $t),
    }));
  }

  return { operators, valueEditorType, inputType, dataType, values };
};

export function getFieldLabel({ field, value, $t }) {
  if (value != null && messages[`${field}${capitalize(value.toString())}`]) {
    return $t(messages[`${field}${capitalize(value.toString())}`]);
  }

  return messages[field] ? $t(messages[field]) : field;
}

const FILTER_FIELD_CATEGORY_ORDER = [
  'visitSession',
  'visitor',
  'device',
  'browser',
  'referrer',
  'campaign',
  'pageview',
  'event',
  'conversion',
  'performance',
  'survey',
];

const getFields = ({ $t, customerId, goalOptions }) => {
  const byCategory = Object.entries(filterFields).reduce((acc, [field, fieldConfig]) => {
    const { category } = fieldConfig;
    if (!acc[category]) acc[category] = [];
    acc[category].push({ field, fieldConfig });
    return acc;
  }, {});

  return FILTER_FIELD_CATEGORY_ORDER.filter((category) => byCategory[category]?.length).map((category) => ({
    label: $t(messages[`filterCategory${upperFirst(category)}`]),
    options: byCategory[category].map(({ field, fieldConfig }) => {
      const { category: _omit, ...rest } = fieldConfig;
      return {
        ...getFieldProps({ field, fieldConfig, $t, goalOptions }),
        ...rest,
        customerId,
        name: field,
        label: getFieldLabel({ field, value: fieldConfig.value, $t }),
      };
    }),
  }));
};

const controlElements = {
  valueEditor: CustomValueEditor,
  fieldSelector: SearchableValueSelector,
};

const QueryBuilderInput = ({ value, onChange, fields, format = 'json_without_ids', customerId: customerIdProp }) => {
  const [query, setQuery] = useState(value || emptyQuery);
  const $t = useFormatMessage();
  const { user } = useUser();
  const customerId = customerIdProp || user?.globalSettings?.selectedCustomer;
  const { options: goalOptions } = useMatomoGoalOptions(customerId);

  useEffect(() => {
    if (value == null) return;
    // Keep QueryBuilder's generated rule ids in local state. The form stores
    // json_without_ids; writing that back remounts each rule and steals focus
    // from the value input (EXT-205).
    setQuery((current) => (isSameQuery(current, value) ? current : value));
  }, [value]);

  const onQueryChange = useCallback((updatedQuery) => {
    setQuery(updatedQuery);
    if (!onChange || isSameQuery(updatedQuery, query)) return;
    onChange(JSON.parse(formatQuery(updatedQuery, format)));
  }, [format, onChange, query]);

  const queryFields = useMemo(() => {
    if (fields) return fields;

    return [
      {
        label: 'Segments',
        options: [{
          label: 'Predefined segment',
          name: 'segment',
          operators: equalOperators,
          type: 'segment',
        }],
      },
      ...getFields({ $t, customerId, goalOptions }),
    ];
  }, [fields, $t, customerId, goalOptions]);

  return (
    <QueryBuilderAntD>
      <QueryBuilder
        fields={queryFields}
        query={query}
        onQueryChange={onQueryChange}
        showNotToggle
        listsAsArrays
        addRuleToNewGroups
        controlElements={controlElements}
        getDefaultValue={getDefaultValue}
      // parseNumbers="strict-limited"
      />
    </QueryBuilderAntD>
  );
};

export default QueryBuilderInput;
